import { useState, useRef, useEffect, useCallback } from "react";
import { useI18n } from "../i18n";
import { TermsContent } from "./TermsContent";
import type { TermsType } from "../api/types";

interface TermsModalProps {
  isOpen: boolean;
  termsType: TermsType | null;
  isLoading: boolean;
  onAccept: () => void;
  onClose: () => void;
}

export function TermsModal({
  isOpen,
  termsType,
  isLoading,
  onAccept,
  onClose,
}: TermsModalProps) {
  const { t } = useI18n();
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const isTOS = termsType === "TOS";
  const canClose = !isTOS && !isLoading;

  // 重置捲動狀態（當 Modal 打開時）
  useEffect(() => {
    if (isOpen && isTOS) {
      setHasScrolledToBottom(false);
    }
  }, [isOpen, isTOS]);

  // 檢查是否已捲動到底部
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    // 留 20px 的容差
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 20;

    if (isAtBottom && !hasScrolledToBottom) {
      setHasScrolledToBottom(true);
    }
  }, [hasScrolledToBottom]);

  // TOS 需要捲動到底部才能同意
  const canAccept = isTOS ? hasScrolledToBottom : true;

  if (!isOpen || !termsType) return null;

  const getTitle = () => {
    switch (termsType) {
      case "TOS":
        return t.termsModal?.tosTitle || "Terms of Service";
      case "BUYER_DISCLAIMER":
        return t.termsModal?.buyerTitle || "Buyer Disclaimer";
      case "SELLER_DISCLAIMER":
        return t.termsModal?.sellerTitle || "Seller Disclaimer";
    }
  };

  const renderContent = () => {
    switch (termsType) {
      case "TOS":
        return (
          <div className="terms-modal-content-tos">
            <TermsContent />
          </div>
        );
      case "BUYER_DISCLAIMER":
        return (
          <div className="terms-modal-content-disclaimer">
            <p className="disclaimer-intro">{t.termsModal?.buyerContent || "Before purchasing, please understand:"}</p>
            <ul>
              <li>{t.termsModal?.buyerItem1 || "Payments are processed via blockchain and are final."}</li>
              <li>{t.termsModal?.buyerItem2 || "The platform does not guarantee content quality."}</li>
              <li>{t.termsModal?.buyerItem3 || "Disputes should be resolved directly with the seller."}</li>
              <li>{t.termsModal?.buyerItem4 || "Review content description before purchasing."}</li>
              {t.termsModal?.buyerItem5 && <li>{t.termsModal.buyerItem5}</li>}
              {t.termsModal?.buyerItem6 && <li>{t.termsModal.buyerItem6}</li>}
            </ul>
          </div>
        );
      case "SELLER_DISCLAIMER":
        return (
          <div className="terms-modal-content-disclaimer">
            <p className="disclaimer-intro">{t.termsModal?.sellerContent || "Before listing, please understand:"}</p>
            <ul>
              <li>{t.termsModal?.sellerItem1 || "You are responsible for the content you sell."}</li>
              <li>{t.termsModal?.sellerItem2 || "Content must comply with all applicable laws."}</li>
              <li>{t.termsModal?.sellerItem3 || "You must have rights to sell the content."}</li>
              <li>{t.termsModal?.sellerItem4 || "A 5% tool usage fee applies to all sales."}</li>
              {t.termsModal?.sellerItem5 && <li>{t.termsModal.sellerItem5}</li>}
              {t.termsModal?.sellerItem6 && <li>{t.termsModal.sellerItem6}</li>}
            </ul>
          </div>
        );
    }
  };

  const getAcceptButtonText = () => {
    if (isLoading) {
      return t.termsModal?.processing || "Processing...";
    }
    switch (termsType) {
      case "TOS":
        return t.termsModal?.tosAcceptBtn || "I Agree";
      case "BUYER_DISCLAIMER":
        return t.termsModal?.buyerAcceptBtn || "I Understand, Continue";
      case "SELLER_DISCLAIMER":
        return t.termsModal?.sellerAcceptBtn || "I Understand, Continue";
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && canClose) {
      onClose();
    }
  };

  return (
    <div className="terms-modal-overlay" onClick={handleOverlayClick}>
      <div className="terms-modal">
        <div className="terms-modal-header">
          <h2>{getTitle()}</h2>
          {canClose && (
            <button className="terms-modal-close" onClick={onClose} aria-label="Close">
              <XIcon />
            </button>
          )}
        </div>

        <div
          className="terms-modal-body"
          ref={isTOS ? scrollContainerRef : undefined}
          onScroll={isTOS ? handleScroll : undefined}
        >
          {renderContent()}
        </div>

        <div className="terms-modal-footer">
          {/* TOS 需要捲動到底部的提示 */}
          {isTOS && !hasScrolledToBottom && (
            <p className="terms-scroll-hint">
              <ScrollDownIcon />
              {t.termsModal?.scrollToRead || "Please scroll down to read the full terms"}
            </p>
          )}
          {canClose && (
            <button
              className="terms-modal-cancel"
              onClick={onClose}
              disabled={isLoading}
            >
              {t.termsModal?.cancel || "Cancel"}
            </button>
          )}
          <button
            className="terms-modal-accept"
            onClick={onAccept}
            disabled={isLoading || !canAccept}
          >
            {isLoading && <span className="btn-spinner" />}
            {getAcceptButtonText()}
          </button>
        </div>
      </div>
    </div>
  );
}

function XIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function ScrollDownIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M12 5v14M19 12l-7 7-7-7" />
    </svg>
  );
}
